import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { t } from 'i18next';
import axios from 'src/config/axios';
import {
    OnboardingFilters,
    OnboardingInitialState,
    GetOnboardingFiltersResponse,
    GetOnboardingApplicantsResponse,
    GetOnboardingRequestsCountResponse,
    OnboardingApplicant,
} from 'src/utils/interfaces/onboarding/OnboardingTypes';
import { ONBOARDING_PAGE_SIZE } from 'src/utils/constants/constants';
import Toast from 'src/shared/toast/Toast';
import { RootState } from 'src/redux/store';

const appKey = 'appOnboarding';
const ONBOARDING_API_BASE = "pa/ft/data-collection-tasks";

export const defaultOnboardingFilters: OnboardingFilters = {
    page: 1,
    skip: 0,
    limit: ONBOARDING_PAGE_SIZE,
};

export const getOnboardingApplicants = createAsyncThunk<
    GetOnboardingApplicantsResponse,
    OnboardingFilters | undefined,
    { state: RootState }
>(`${appKey}/getOnboardingApplicants`, async (filters) => {
    const f = { ...defaultOnboardingFilters, ...filters };

    const queryParams: Record<string, any> = {
        skip: f.skip ?? 0,
        limit: f.limit ?? ONBOARDING_PAGE_SIZE,
    };

    if (f.search) queryParams.search = f.search;
    if (f.requestStatus?.length) queryParams.statuses = f.requestStatus;
    if (f.requestDateFrom != null) queryParams.requestDateFrom = f.requestDateFrom;
    if (f.requestDateTo != null) queryParams.requestDateTo = f.requestDateTo;
    if (f.jobIds?.length) queryParams.jobTitleExternalIds = f.jobIds;
    if (f.jobPostIds?.length) queryParams.jobPostExternalIds = f.jobPostIds;
    if (f.ownerIds?.length) queryParams.ownerExternalIds = f.ownerIds;

    const response = await axios.get('pa/ft/data-collection/requests/list', { params: queryParams });
    const data = response.data?.data ?? {};

    return { applicants: data.applicants ?? [], total: data.total ?? 0 };
});

export const getOnboardingFilters = createAsyncThunk<GetOnboardingFiltersResponse>(
    `${appKey}/getOnboardingFilters`,
    async () => {
        const response = await axios.get('pa/ft/data-collection/requests/filter-options');
        const data = response.data?.data ?? {};
        const isAr = !localStorage.getItem('i18nextLng')?.includes('en');
        return {
            jobTitles: (data.jobTitles ?? []).map((jt: any) => ({
                id: jt.id,
                name: isAr ? (jt.nameAr ?? jt.nameEn ?? jt.id) : (jt.nameEn ?? jt.nameAr ?? jt.id),
            })),
            owners: data.owners ?? [],
        };
    }
);

export const downloadOnboardingAttachments = createAsyncThunk<void, string>(
    `${appKey}/downloadOnboardingAttachments`,
    async (requestExternalId) => {
        const response = await axios.get(
            `pa/ft/data-collection/requests/${requestExternalId}/documents/download`,
            { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `data-collection-request-${requestExternalId}-documents.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }
);

export const downloadBulkOnboardingAttachments = createAsyncThunk<void, string[]>(
    `${appKey}/downloadBulkOnboardingAttachments`,
    async (requestExternalIds) => {
        const response = await axios.post(
            'pa/ft/data-collection/requests/documents/bulk-download',
            { requestExternalIds },
            { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `data-collection-documents-${Date.now()}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }
);

export const downloadBulkOnboardingFilesExcel = createAsyncThunk<void, string[]>(
    `${appKey}/downloadBulkOnboardingFilesExcel`,
    async (requestExternalIds) => {
        const response = await axios.post(
            'pa/ft/data-collection/requests/files-excel/download',
            { requestExternalIds },
            { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `data-collection-files-excel-${Date.now()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }
);

export const exportToQiwaExcel = createAsyncThunk<void, string[]>(
    `${appKey}/exportToQiwaExcel`,
    async (requestExternalIds) => {
        const response = await axios.post(
            'pa/ft/data-collection/requests/excel/download',
            { requestExternalIds },
            { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'qiwa-export.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }
);

export const resendOnboardingRequest = createAsyncThunk<void, string>(
    `${appKey}/resendOnboardingRequest`,
    async (requestExternalId) => {
        await axios.post('pa/ft/data-collection/requests/resend', { requestExternalIds: [requestExternalId] });
    }
);

export const resendBulkOnboardingRequests = createAsyncThunk<void, string[]>(
    `${appKey}/resendBulkOnboardingRequests`,
    async (requestExternalIds) => {
        await axios.post('pa/ft/data-collection/requests/resend', { requestExternalIds });
    }
);

export const getOnboardingRequestsCount = createAsyncThunk<GetOnboardingRequestsCountResponse>(
    `${appKey}/getOnboardingRequestsCount`,
    async () => {
        const response = await axios.get('pa/ft/data-collection/requests/count');
        const data = response.data?.data ?? {};
        return { count: data.count ?? 0 };
    }
);

export const setOnboardingFilters = createAction<Partial<OnboardingFilters>>(`${appKey}/setOnboardingFilters`);
export const forceSetOnboardingFilters = createAction<OnboardingFilters>(`${appKey}/forceSetOnboardingFilters`);
export const setSelectedOnboardingApplicantIds = createAction<string[]>(`${appKey}/setSelectedOnboardingApplicantIds`);
export const setShowExportToQiwaModal = createAction<boolean>(`${appKey}/setShowExportToQiwaModal`);
export const setCancelRequestModal = createAction<{ show: boolean; applicant?: OnboardingApplicant | null }>(
    `${appKey}/setCancelRequestModal`
);
export const setRecollectModal = createAction<{ show: boolean; data?: { requestExternalId: string; taskExternalId: string; taskName: string } | null }>(
    `${appKey}/setRecollectModal`
);

const initialState: OnboardingInitialState = {
    applicants: [],
    total: 0,
    requestsCount: 0,
    isLoading: false,
    isLoadingCount: false,
    filters: defaultOnboardingFilters,
    selectedApplicantIds: [],
    filterOptions: null,
    isLoadingFilters: false,
    isDownloadingAttachments: false,
    isDownloadingFilesExcel: false,
    isResendingBulkRequests: false,
    isExportingToQiwa: false,
    showExportToQiwaModal: false,
    showCancelRequestModal: false,
    cancelRequestApplicant: null,
    showRecollectModal: false,
    recollectModalData: null,
};

export const onboardingSlice = createSlice({
    name: appKey,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getOnboardingApplicants.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getOnboardingApplicants.fulfilled, (state, action) => {
                state.applicants = action.payload.applicants;
                state.total = action.payload.total;
                state.isLoading = false;
            })
            .addCase(getOnboardingApplicants.rejected, (state, action) => {
                state.isLoading = false;
                Toast.Error({ content: action.error?.message ?? t('error_loading_onboarding_applicants') });
            })
            .addCase(getOnboardingRequestsCount.pending, (state) => {
                state.isLoadingCount = true;
            })
            .addCase(getOnboardingRequestsCount.fulfilled, (state, action) => {
                state.requestsCount = action.payload.count;
                state.isLoadingCount = false;
            })
            .addCase(getOnboardingRequestsCount.rejected, (state) => {
                state.isLoadingCount = false;
            })
            .addCase(getOnboardingFilters.pending, (state) => {
                state.isLoadingFilters = true;
            })
            .addCase(getOnboardingFilters.fulfilled, (state, action) => {
                state.filterOptions = action.payload;
                state.isLoadingFilters = false;
            })
            .addCase(getOnboardingFilters.rejected, (state, action) => {
                state.isLoadingFilters = false;
                Toast.Error({ content: action.error?.message ?? t('error_loading_onboarding_filter_options') });
            })
            .addCase(setOnboardingFilters, (state, action) => {
                if (action.payload && typeof action.payload === 'object') {
                    state.filters = { ...state.filters, ...action.payload };
                }
            })
            .addCase(forceSetOnboardingFilters, (state, action) => {
                state.filters = action.payload;
            })
            .addCase(setSelectedOnboardingApplicantIds, (state, action) => {
                state.selectedApplicantIds = action.payload;
            })
            .addCase(setShowExportToQiwaModal, (state, action) => {
                state.showExportToQiwaModal = action.payload;
            })
            .addCase(setCancelRequestModal, (state, action) => {
                state.showCancelRequestModal = action.payload.show;
                state.cancelRequestApplicant = action.payload.applicant ?? null;
            })
            .addCase(setRecollectModal, (state, action) => {
                state.showRecollectModal = action.payload.show;
                state.recollectModalData = action.payload.data ?? null;
            })
            .addCase(downloadOnboardingAttachments.pending, (state) => {
                state.isDownloadingAttachments = true;
            })
            .addCase(downloadOnboardingAttachments.fulfilled, (state) => {
                state.isDownloadingAttachments = false;
                Toast.Success({ content: t('onboarding_download_started') });
            })
            .addCase(downloadOnboardingAttachments.rejected, (state, action) => {
                state.isDownloadingAttachments = false;
                Toast.Error({ content: action.error?.message ?? t('error_downloading_onboarding_attachments') });
            })
            .addCase(downloadBulkOnboardingAttachments.pending, (state) => {
                state.isDownloadingAttachments = true;
            })
            .addCase(downloadBulkOnboardingAttachments.fulfilled, (state) => {
                state.isDownloadingAttachments = false;
                Toast.Success({ content: t('onboarding_download_started') });
            })
            .addCase(downloadBulkOnboardingAttachments.rejected, (state, action) => {
                state.isDownloadingAttachments = false;
                Toast.Error({ content: action.error?.message ?? t('error_downloading_onboarding_attachments') });
            })
            .addCase(downloadBulkOnboardingFilesExcel.pending, (state) => {
                state.isDownloadingFilesExcel = true;
            })
            .addCase(downloadBulkOnboardingFilesExcel.fulfilled, (state) => {
                state.isDownloadingFilesExcel = false;
                Toast.Success({ content: t('onboarding_download_started') });
            })
            .addCase(downloadBulkOnboardingFilesExcel.rejected, (state, action) => {
                state.isDownloadingFilesExcel = false;
                Toast.Error({ content: action.error?.message ?? t('error_downloading_onboarding_files_excel') });
            })
            .addCase(exportToQiwaExcel.pending, (state) => {
                state.isExportingToQiwa = true;
            })
            .addCase(exportToQiwaExcel.fulfilled, (state) => {
                state.isExportingToQiwa = false;
                Toast.Success({ content: t('onboarding_export_downloaded') });
            })
            .addCase(exportToQiwaExcel.rejected, (state, action) => {
                state.isExportingToQiwa = false;
                Toast.Error({ content: action.error?.message ?? t('error_exporting_onboarding_to_qiwa') });
            })
            .addCase(resendOnboardingRequest.fulfilled, () => {
                Toast.Success({ content: t('onboarding_request_resent') });
            })
            .addCase(resendOnboardingRequest.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message ?? t('error_resending_onboarding_request') });
            })
            .addCase(resendBulkOnboardingRequests.pending, (state) => {
                state.isResendingBulkRequests = true;
            })
            .addCase(resendBulkOnboardingRequests.fulfilled, (state) => {
                state.isResendingBulkRequests = false;
                Toast.Success({ content: t('onboarding_requests_resent') });
            })
            .addCase(resendBulkOnboardingRequests.rejected, (state, action) => {
                state.isResendingBulkRequests = false;
                Toast.Error({ content: action.error?.message ?? t('error_resending_onboarding_requests') });
            });
    },
});

export default onboardingSlice.reducer;
